import { useEffect, useRef } from 'react';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { trackEvent } from '@/lib/analytics';

interface AmbientVideoProps {
  /** MP4 source (required — universal fallback). */
  srcMp4: string;
  /** WebM source (smaller; served first where supported). */
  srcWebm?: string;
  /** Poster image for first paint and video-off fallbacks. */
  poster: string;
  className?: string;
  /** Analytics label fired once on first successful play (video_play). */
  analyticsLabel?: string;
}

/**
 * Decorative ambient background video (muted, looping, no controls).
 *
 * Playback etiquette:
 * - Renders nothing under prefers-reduced-motion (caller may also gate).
 * - Skips the video entirely on Save-Data or 2G-class connections — the
 *   poster (set as a background by the caller's CSS or the poster attr)
 *   remains the visual.
 * - Pauses when it leaves the viewport, resumes when it returns.
 * - Fires a single `video_play` analytics event on first playback.
 *
 * Decorative only: aria-hidden, no controls, never carries information.
 */
const AmbientVideo = ({
  srcMp4,
  srcWebm,
  poster,
  className,
  analyticsLabel,
}: AmbientVideoProps) => {
  const reduced = useReducedMotion();
  const videoRef = useRef<HTMLVideoElement>(null);
  const playTracked = useRef(false);

  // Save-Data / slow-connection guard (evaluated once per mount).
  const connection = (
    navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }
  ).connection;
  const slowConnection =
    !!connection &&
    (connection.saveData === true ||
      connection.effectiveType === 'slow-2g' ||
      connection.effectiveType === '2g');

  useEffect(() => {
    const video = videoRef.current;
    if (!video || reduced || slowConnection) return;

    const onPlay = () => {
      if (!playTracked.current && analyticsLabel) {
        playTracked.current = true;
        trackEvent('video_play', { video_label: analyticsLabel, video_kind: 'ambient' });
      }
    };
    video.addEventListener('play', onPlay);

    // Pause offscreen, resume when visible again.
    let io: IntersectionObserver | undefined;
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              video.play().catch(() => {
                /* autoplay veto — poster remains, nothing breaks */
              });
            } else {
              video.pause();
            }
          });
        },
        { threshold: 0.05 },
      );
      io.observe(video);
    }

    return () => {
      video.removeEventListener('play', onPlay);
      io?.disconnect();
    };
  }, [reduced, slowConnection, analyticsLabel]);

  if (reduced || slowConnection) return null;

  return (
    <video
      ref={videoRef}
      className={className}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      poster={poster}
      aria-hidden="true"
      tabIndex={-1}
    >
      {srcWebm && <source src={srcWebm} type="video/webm" />}
      <source src={srcMp4} type="video/mp4" />
    </video>
  );
};

export default AmbientVideo;
